import React, { useEffect, useMemo, useState } from 'react';
import { Row, Col, Button, Form, Spinner } from 'react-bootstrap';
import { PencilFill, CheckLg, XLg, PlusCircle, Trash, ArrowRight } from 'react-bootstrap-icons';
import { useAuth } from '../../contexts/SecurityContext';
import RoleGuard from '../security/RoleGuard';
import SectionControls from '../SectionControls';
import { updateSubmitCtaSection } from '../../services/contentService';
import './SubmitCtaBlock.scss';

export function defaultSubmitCtaData() {
  return {
    title: 'Ready to submit your solution?',
    subtitle: 'Log in to the platform to review materials and submit your proposal',
    steps: ['Create account', 'Download technical package', 'Submit proposal'],
    buttonText: 'Submit application',
    buttonLinkUrl: '/user',
    openInNewTab: false,
  };
}

export function SubmitCtaView({ data }) {
  const steps = Array.isArray(data?.steps) ? data.steps : [];
  const url = data?.buttonLinkUrl || '#';
  const target = data?.openInNewTab ? '_blank' : '_self';
  const rel = data?.openInNewTab ? 'noopener noreferrer' : undefined;
  return (
    <div className="submit-cta-block">
      {data?.title ? <div className="submit-cta-block__title">{data.title}</div> : null}
      {data?.subtitle ? <div className="submit-cta-block__subtitle">{data.subtitle}</div> : null}
      {steps.length > 0 ? (
        <div className="submit-cta-block__steps">
          {steps.map((step, idx) => (
            <React.Fragment key={idx}>
              <div className="submit-cta-block__step">
                <span className="submit-cta-block__step-number">{idx + 1}</span>
                <span className="submit-cta-block__step-label">{step}</span>
              </div>
              {idx < steps.length - 1 ? (
                <ArrowRight className="submit-cta-block__arrow" />
              ) : null}
            </React.Fragment>
          ))}
        </div>
      ) : null}
      {data?.buttonText ? (
        <a href={url} target={target} rel={rel} className="submit-cta-block__button">
          {data.buttonText}
        </a>
      ) : null}
    </div>
  );
}

function SubmitCtaBlock({
  pageId, section, onMoveUp, onMoveDown, onDelete, isFirstSection, isLastSection,
}) {
  const { user } = useAuth();
  const initialData = useMemo(() => section?.data || defaultSubmitCtaData(), [section]);
  const [data, setData] = useState(initialData);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => { setData(section?.data || defaultSubmitCtaData()); }, [section]);

  async function handleSave() {
    setIsSaving(true);
    try {
      const modifiedBy = user?.attributes?.email || user?.username || 'user';
      await updateSubmitCtaSection(pageId, section.position, data, modifiedBy);
      setIsEditing(false);
    } catch (e) {
      console.error('Error saving submit CTA:', e);
      // eslint-disable-next-line no-alert
      alert('Failed to save section.');
    } finally {
      setIsSaving(false);
    }
  }

  function handleCancel() { setData(section?.data || defaultSubmitCtaData()); setIsEditing(false); }
  function updateField(field, value) { setData((prev) => ({ ...prev, [field]: value })); }
  function updateStep(idx, value) {
    setData((prev) => {
      const next = { ...prev, steps: [...(prev.steps || [])] };
      next.steps[idx] = value;
      return next;
    });
  }
  function addStep() { setData((prev) => ({ ...prev, steps: [...(prev.steps || []), 'Step'] })); }
  function removeStep(idx) {
    setData((prev) => {
      const next = { ...prev, steps: [...(prev.steps || [])] };
      next.steps.splice(idx, 1);
      return next;
    });
  }

  if (isEditing) {
    return (
      <div className="submit-cta-block submit-cta-block--editing">
        <Row className="g-2 mb-3">
          <Col md={6}>
            <Form.Label>Title</Form.Label>
            <Form.Control type="text" value={data.title || ''} onChange={(e) => updateField('title', e.target.value)} />
          </Col>
          <Col md={6}>
            <Form.Label>Subtitle (optional)</Form.Label>
            <Form.Control type="text" value={data.subtitle || ''} onChange={(e) => updateField('subtitle', e.target.value)} />
          </Col>
        </Row>

        <Form.Label>Steps</Form.Label>
        {(data.steps || []).map((step, idx) => (
          <div key={idx} className="submit-cta-block__editor-step">
            <Row className="g-2 align-items-center">
              <Col xs="auto"><strong>Step {idx + 1}:</strong></Col>
              <Col>
                <Form.Control type="text" value={step} onChange={(e) => updateStep(idx, e.target.value)} />
              </Col>
              <Col xs="auto">
                <Button variant="outline-danger" size="sm" onClick={() => removeStep(idx)}><Trash /></Button>
              </Col>
            </Row>
          </div>
        ))}
        <Button variant="outline-primary" size="sm" onClick={addStep} className="mb-3">
          <PlusCircle /> Add step
        </Button>

        <Row className="g-2">
          <Col md={4}>
            <Form.Label>Button text</Form.Label>
            <Form.Control type="text" value={data.buttonText || ''} onChange={(e) => updateField('buttonText', e.target.value)} />
          </Col>
          <Col md={6}>
            <Form.Label>Button link URL</Form.Label>
            <Form.Control type="text" value={data.buttonLinkUrl || ''} onChange={(e) => updateField('buttonLinkUrl', e.target.value)} />
          </Col>
          <Col md={2} className="d-flex align-items-end">
            <Form.Check
              type="checkbox"
              id={`submitcta-newtab-${section?.position}`}
              label="New tab"
              checked={!!data.openInNewTab}
              onChange={(e) => updateField('openInNewTab', e.target.checked)}
            />
          </Col>
        </Row>

        <div className="submit-cta-block__editor-actions">
          <Button variant="success" onClick={handleSave} disabled={isSaving}>
            {isSaving ? (<><Spinner as="span" animation="border" size="sm" className="me-2" />Saving…</>) : (<><CheckLg /> Save</>)}
          </Button>
          <Button variant="secondary" onClick={handleCancel} disabled={isSaving}><XLg /> Cancel</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="submit-cta-block-wrapper">
      <SubmitCtaView data={data} />
      <RoleGuard role="admin">
        <div className="admin-controls">
          <Button variant="outline-secondary" size="sm" className="edit-btn" onClick={() => setIsEditing(true)}>
            <PencilFill /> Edit CTA
          </Button>
          <SectionControls onMoveUp={onMoveUp} onMoveDown={onMoveDown} onDelete={onDelete} isFirstSection={isFirstSection} isLastSection={isLastSection} />
        </div>
      </RoleGuard>
    </div>
  );
}

export default SubmitCtaBlock;
